/**
 * ViatorWidget — "Nearby Experiences" affiliate card section.
 *
 * Shows curated Viator wine-country tour cards for the winery's region.
 * Links open Viator search pre-filtered by destination.
 *
 * To earn affiliate commissions: add your Viator Publisher ID to
 * REACT_APP_VIATOR_PID in frontend/.env  (e.g. REACT_APP_VIATOR_PID=P00123456)
 * and the links will automatically include your tracking parameter.
 */
import { MapPin, Star, Clock } from 'lucide-react';

const VIATOR_PID = process.env.REACT_APP_VIATOR_PID || '';

const pidParam = VIATOR_PID ? `&pid=${VIATOR_PID}&mcid=42383&medium=link&campaign=wineryfinderapp` : '';

// Curated top-rated Napa / Sonoma tour cards with real Viator slugs
const NAPA_TOURS = [
  {
    id: 'hot-air-balloon',
    title: 'Napa Valley Hot Air Balloon Ride with Champagne Brunch',
    type: 'Adventure',
    duration: '3–4 hours',
    rating: 4.8,
    reviews: 2100,
    price: 259,
    image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&q=75',
    url: `https://www.viator.com/tours/Napa-Valley/Napa-Valley-Hot-Air-Balloon-Ride-with-Champagne-Brunch/d5765-3335BALLOON${pidParam}`,
  },
  {
    id: 'wine-tour',
    title: 'Small-Group Napa Valley Wine Tour with Tastings',
    type: 'Wine Tour',
    duration: '8 hours',
    rating: 4.9,
    reviews: 1540,
    price: 159,
    image: 'https://images.unsplash.com/photo-1510812431401-41d2bd2722f3?w=400&q=75',
    url: `https://www.viator.com/tours/Napa-Valley/Small-Group-Napa-Valley-Wine-Tour/d5765-5335WINE${pidParam}`,
  },
  {
    id: 'wine-train',
    title: 'Napa Valley Wine Train Excursion',
    type: 'Scenic Rail',
    duration: '3 hours',
    rating: 4.7,
    reviews: 3200,
    price: 124,
    image: 'https://images.unsplash.com/photo-1474487548417-781cb71495f3?w=400&q=75',
    url: `https://www.viator.com/tours/Napa-Valley/Napa-Valley-Wine-Train/d5765-2585TRAIN${pidParam}`,
  },
];

const SONOMA_TOURS = [
  {
    id: 'sonoma-wine',
    title: 'Sonoma Valley Wine Country Full-Day Tour',
    type: 'Wine Tour',
    duration: '8 hours',
    rating: 4.8,
    reviews: 980,
    price: 149,
    image: 'https://images.unsplash.com/photo-1568213816046-0ee1c42bd559?w=400&q=75',
    url: `https://www.viator.com/tours/Sonoma/Sonoma-Valley-Wine-Country-Tour/d23049-3345SONOMA${pidParam}`,
  },
  {
    id: 'sonoma-bike',
    title: 'Bike & Sip: Sonoma Valley Wine Tour by Bicycle',
    type: 'Cycling',
    duration: '4 hours',
    rating: 4.9,
    reviews: 620,
    price: 99,
    image: 'https://images.unsplash.com/photo-1541625602330-2277a4c46182?w=400&q=75',
    url: `https://www.viator.com/tours/Sonoma/Bike-and-Sip-Wine-Tour/d23049-7765BIKE${pidParam}`,
  },
  {
    id: 'sonoma-balloon',
    title: 'Sonoma Wine Country Hot Air Balloon Flight',
    type: 'Adventure',
    duration: '3 hours',
    rating: 4.7,
    reviews: 430,
    price: 239,
    image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&q=75',
    url: `https://www.viator.com/tours/Sonoma/Sonoma-Hot-Air-Balloon/d23049-9921BALLOON${pidParam}`,
  },
];

const TourCard = ({ tour }) => (
  <a
    href={tour.url}
    target="_blank"
    rel="noopener noreferrer sponsored"
    data-testid={`viator-tour-${tour.id}`}
    className="group flex flex-col bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden hover:shadow-md hover:-translate-y-0.5 transition-all duration-200"
  >
    <div className="relative h-36 overflow-hidden">
      <img
        src={tour.image}
        alt={tour.title}
        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
        loading="lazy"
      />
      <span className="absolute top-2 left-2 bg-white/90 backdrop-blur-sm text-[10px] font-semibold text-gray-700 px-2 py-0.5 rounded-full">
        {tour.type}
      </span>
    </div>
    <div className="p-3 flex flex-col flex-1">
      <p className="text-[13px] font-semibold text-gray-900 leading-snug line-clamp-2 flex-1">
        {tour.title}
      </p>
      <div className="flex items-center gap-3 mt-2">
        <span className="flex items-center gap-1 text-[11px] text-amber-500 font-medium">
          <Star size={11} fill="currentColor" />
          {tour.rating}
          <span className="text-gray-400 font-normal">({tour.reviews.toLocaleString()})</span>
        </span>
        <span className="flex items-center gap-1 text-[11px] text-gray-500">
          <Clock size={11} />
          {tour.duration}
        </span>
      </div>
      <div className="flex items-center justify-between mt-2.5 pt-2 border-t border-gray-100">
        <span className="text-[11px] text-gray-500">From</span>
        <span className="text-[15px] font-bold text-wine-700">${tour.price}</span>
      </div>
    </div>
  </a>
);

const ViatorWidget = ({ region = 'Napa Valley' }) => {
  const isNapa = region.toLowerCase().includes('napa');
  const tours = isNapa ? NAPA_TOURS : SONOMA_TOURS;
  const destination = isNapa ? 'Napa-Valley' : 'Sonoma';
  const searchUrl = `https://www.viator.com/searchResults/all?text=${destination.replace('-', '+')}+wine+tours${pidParam}`;

  return (
    <section className="mt-10" data-testid="viator-widget">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-bold text-gray-900">Nearby Experiences</h2>
          <p className="text-sm text-gray-500 flex items-center gap-1 mt-0.5">
            <MapPin size={13} />
            Top-rated {region} tours via Viator
          </p>
        </div>
        <a
          href={searchUrl}
          target="_blank"
          rel="noopener noreferrer sponsored"
          className="text-xs font-medium text-wine-600 hover:text-wine-700 underline underline-offset-2"
          data-testid="viator-see-all"
        >
          See all tours →
        </a>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {tours.map((tour) => <TourCard key={tour.id} tour={tour} />)}
      </div>

      <p className="text-[10px] text-gray-400 mt-3 text-right">
        Powered by Viator · booking fees may apply
      </p>
    </section>
  );
};

export default ViatorWidget;
